import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { supabase } from '../supabase'
import Topbar from '../components/Topbar'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { getCurrencySymbol } from '../utils/currencies'
import { useTranslation } from '../i18n/LanguageContext'
import {
  LineChart, Line, XAxis, YAxis, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts'
import CourierExportModal from '../components/CourierExportModal'

const ORDERS_PER_PAGE = 10
const STORAGE_KEY = 'pc_orders_columns_v1'

// ── Tara star icon — used wherever Tara-sourced content needs marking ──────
function TaraStarIcon({ size = 14, color = '#2DD4B0' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
      <path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={color}/>
      <path d="M19 3C19 3 19.4 5.2 20.2 6C21 6.8 23 7 23 7C23 7 21 7.2 20.2 8C19.4 8.8 19 11 19 11C19 11 18.6 8.8 17.8 8C17 7.2 15 7 15 7C15 7 17 6.8 17.8 6C18.6 5.2 19 3 19 3Z" fill={color} opacity="0.7"/>
      <path d="M5 16C5 16 5.25 17.3 5.75 17.75C6.25 18.25 7.5 18.5 7.5 18.5C7.5 18.5 6.25 18.75 5.75 19.25C5.25 19.75 5 21 5 21C5 21 4.75 19.75 4.25 19.25C3.75 18.75 2.5 18.5 2.5 18.5C2.5 18.5 3.75 18.25 4.25 17.75C4.75 17.3 5 16 5 16Z" fill={color} opacity="0.5"/>
    </svg>
  )
}

// Small pill badge for Tara-sourced orders — used in table rows + mobile cards
function TaraBadge({ size = 'sm' }) {
  const isSmall = size === 'sm'
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: isSmall ? '1px 6px' : '2px 8px', borderRadius: 'var(--radius-full)', background: 'rgba(45,212,176,0.12)', border: '1px solid rgba(45,212,176,0.3)', flexShrink: 0 }}>
      <TaraStarIcon size={isSmall ? 9 : 11} color="#0D9488" />
      <span style={{ fontSize: isSmall ? '9.5px' : '10.5px', fontWeight: 700, color: '#0D9488', fontFamily: 'var(--font-ui)' }}>Tara</span>
    </span>
  )
}

// ── Column config — width drives grid-template-columns dynamically ─────────────
const ALL_COLUMNS = [
  { id:'sl',       label:'#',        width:'36px',  locked:true },
  { id:'orderId',  label:'Order ID', width:'115px' },
  { id:'customer', label:'Customer', width:'1fr',   locked:true },
  { id:'product',  label:'Product',  width:'1fr',   locked:true },
  { id:'location', label:'Location', width:'125px' },
  { id:'date',     label:'Date',     width:'90px'  },
  { id:'amount',   label:'Amount',   width:'100px', locked:true },
  { id:'status',   label:'Status',   width:'95px',  locked:true },
  { id:'actions',  label:'Actions',  width:'125px', locked:true },
]
const DEFAULT_VISIBLE = Object.fromEntries(ALL_COLUMNS.map(c=>[c.id,true]))

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pre_order:    { bg:'var(--bg-muted)',       text:'var(--text-muted)',   pill:'rgba(13,148,136,0.12)',  pillText:'#0D9488',   label:'Pre-order' },
  received:     { bg:'var(--primary-light)',  text:'var(--primary)',      pill:'var(--primary-light)',   pillText:'var(--primary)',   label:'Received' },
  pending:      { bg:'var(--warning-light)',  text:'var(--warning)',      pill:'var(--warning-light)',   pillText:'var(--warning)',   label:'Pending' },
  confirmed:    { bg:'#FEF3C7',              text:'#92400E',             pill:'#FEF3C7',               pillText:'#92400E',   label:'Confirmed' },
  processing:   { bg:'var(--success-light)',  text:'var(--success)',      pill:'var(--success-light)',   pillText:'var(--success)',   label:'Processing' },
  with_courier: { bg:'var(--purpleLight,#F5F3FF)', text:'#6D28D9',       pill:'#F5F3FF',               pillText:'#6D28D9',   label:'Courier' },
  shipped:      { bg:'var(--success-light)',  text:'var(--success)',      pill:'var(--success-light)',   pillText:'var(--success)',   label:'Shipped' },
  closed:       { bg:'var(--bg-muted)',       text:'var(--text-muted)',   pill:'var(--bg-muted)',        pillText:'var(--text-muted)', label:'Closed' },
  return:       { bg:'var(--error-light)',    text:'var(--error)',        pill:'var(--error-light)',     pillText:'var(--error)',   label:'Return' },
}

const BROAD_SOURCE = {
  online: { label:'Online', color:'var(--primary)', bg:'var(--primary-light)', icon:'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9', sources:['facebook inbox','facebook page','facebook comment','instagram','tiktok','website','customer order form','whatsapp'] },
  phone:  { label:'Phone',  color:'var(--success)', bg:'var(--success-light)', icon:'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z', sources:['phone call'] },
  pos:    { label:'POS',    color:'#7C3AED',        bg:'#F5F3FF',              icon:'M9 7H6a2 2 0 00-2 2v9a2 2 0 002 2h9a2 2 0 002-2v-3M16 5a2 2 0 112 2M16 5H9m7 0l-7 7', sources:['walk-in','at counter','pos','self delivery'] },
  event:  { label:'Event',  color:'var(--warning)', bg:'var(--warning-light)', icon:'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z', sources:['event / fair','event','fair'] },
}

function getBroadSource(src) {
  if (!src) return null
  const l = src.toLowerCase()
  for (const v of Object.values(BROAD_SOURCE)) { if (v.sources.some(s=>l.includes(s))) return v }
  return null
}

function SourceBadge({ orderSource }) {
  const b = getBroadSource(orderSource)
  if (!b) return null
  return (
    <div style={{ display:'inline-flex', alignItems:'center', gap:'4px', padding:'2px 7px', borderRadius:'var(--radius-full)', background:b.bg }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={b.color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d={b.icon}/></svg>
      <span style={{ fontSize:'10px', fontWeight:600, color:b.color, fontFamily:'var(--font-ui)' }}>{b.label}</span>
    </div>
  )
}

function Icon({ path, size=15, color='var(--text-muted)', sw=1.8 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
      <path d={path}/>
    </svg>
  )
}

function FlatIcon({ path, size=14, color='var(--text-muted)' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill={color}>
      <path fillRule="evenodd" d={path} clipRule="evenodd"/>
    </svg>
  )
}

const IC = {
  eye:      'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 12m-3 0a3 3 0 106 0 3 3 0 00-6 0',
  edit:     'M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z',
  download: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M7 10l5 5 5-5 M12 15V3',
  print:    'M6 9V2h12v7 M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2 M6 14h12v8H6z',
  upload:   'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
  search:   'M21 21l-4.35-4.35 M11 19a8 8 0 100-16 8 8 0 000 16z',
  plus:     'M12 4v16m8-8H4',
  warning:  'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
  check:    'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  check2:   'M5 13l4 4L19 7',
  orders:   'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2',
  chevL:    'M15 18l-6-6 6-6',
  chevR:    'M9 18l6-6-6-6',
  clock:    'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  x:        'M6 18L18 6M6 6l12 12',
  calendar: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  image:    'M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2H4zm6 9a3 3 0 100-6 3 3 0 000 6z',
}

const FLAT = {
  orders:  'M9 2a1 1 0 000 2h2a1 1 0 100-2H9zM4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z',
  paid:    'M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z',
  pending: 'M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z',
  warning: 'M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z',
}

// ── Animated counter ──────────────────────────────────────────────────────────
function useCountUp(target, duration=600) {
  const [val, setVal] = useState(0)
  const prev = useRef(0)
  useEffect(() => {
    if (target === 0) { setVal(0); return }
    const start = prev.current
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now-t0)/duration, 1)
      const e = 1-Math.pow(1-p, 3)
      setVal(Math.round(start+(target-start)*e))
      if (p < 1) requestAnimationFrame(tick)
      else prev.current = target
    }
    requestAnimationFrame(tick)
  }, [target, duration])
  return val
}

// ── Stat card ─────────────────────────────────────────────────────────────────
function StatCard({ label, value, iconPath, accentColor, loading }) {
  const counted = useCountUp(loading ? 0 : value)
  return (
    <div className="pc-card pc-card-hover" style={{ padding:'18px 20px' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:'12px' }}>
        <span style={{ fontSize:'12px', fontWeight:500, color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{label}</span>
        <div style={{ width:'32px', height:'32px', borderRadius:'var(--radius-md)', background:accentColor+'18', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
          <FlatIcon path={iconPath} size={15} color={accentColor}/>
        </div>
      </div>
      <div style={{ fontSize:'28px', fontWeight:800, color:'var(--text-primary)', letterSpacing:'-0.5px', lineHeight:1, fontFamily:'var(--font-ui)' }}>
        {loading ? <div className="pc-skeleton" style={{ width:'50%', height:'28px', borderRadius:'6px' }}/> : counted}
      </div>
    </div>
  )
}

// ── Filterable column header — Excel-style ──────────────────────────────────────
function FilterableHeader({ label, filterKey, openFilter, setOpenFilter, value, onChange, type='text', active }) {
  const isOpen = openFilter===filterKey
  return (
    <div style={{ position:'relative', display:'flex', alignItems:'center', gap:'4px' }}>
      <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{label}</span>
      <button onClick={(e)=>{ e.stopPropagation(); setOpenFilter(isOpen?null:filterKey) }}
        style={{ width:'16px', height:'16px', display:'flex', alignItems:'center', justifyContent:'center', border:'none', background:'none', cursor:'pointer', padding:0, flexShrink:0 }}>
        <svg width="10" height="10" viewBox="0 0 20 20" fill={active?'var(--primary)':'var(--text-muted)'}>
          <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 01.8 1.6l-4.3 5.74V16a1 1 0 01-1.45.89l-2-1A1 1 0 018 15v-3.66L3.2 5.6A1 1 0 013 4z" clipRule="evenodd"/>
        </svg>
      </button>
      {isOpen&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'22px', left:0, zIndex:50, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'160px' }}>
          {type==='text'&&(
            <input type="text" autoFocus value={value} onChange={e=>onChange(e.target.value)} placeholder={`Filter ${label.toLowerCase()}...`}
              style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor='var(--border-focus)'}
              onBlur={e=>e.target.style.borderColor='var(--border)'}/>
          )}
          {type==='range'&&(
            <div style={{ display:'flex', flexDirection:'column', gap:'6px' }}>
              <input type="number" value={value.min} onChange={e=>onChange({...value,min:e.target.value})} placeholder="Min"
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
              <input type="number" value={value.max} onChange={e=>onChange({...value,max:e.target.value})} placeholder="Max"
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', padding:'6px 8px', fontSize:'12px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}/>
            </div>
          )}
          {active&&(
            <button onClick={()=>{ onChange(type==='range'?{min:'',max:''}:''); setOpenFilter(null) }}
              style={{ marginTop:'8px', width:'100%', padding:'5px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'11px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
              Clear
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Columns visibility dropdown ─────────────────────────────────────────────────
function ColumnsMenu({ visible, setVisible, open, setOpen }) {
  const hiddenCount = ALL_COLUMNS.filter(c=>!c.locked&&!visible[c.id]).length
  return (
    <div style={{ position:'relative' }}>
      <button onClick={(e)=>{ e.stopPropagation(); setOpen(o=>!o) }}
        style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hiddenCount>0?'var(--border-focus)':'var(--border)'), background:hiddenCount>0?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hiddenCount>0?'var(--primary)':'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill={hiddenCount>0?'var(--primary)':'var(--text-primary)'}>
          <path fillRule="evenodd" d="M4 3a1 1 0 00-1 1v16a1 1 0 001 1h3V3H4zm5 0v18h2V3H9zm4 0v18h7a1 1 0 001-1V4a1 1 0 00-1-1h-7z" clipRule="evenodd"/>
        </svg>
        Columns
        {hiddenCount>0&&(
          <span style={{ fontSize:'10px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'var(--primary)', color:'white' }}>{hiddenCount} hidden</span>
        )}
      </button>
      {open&&(
        <div onClick={e=>e.stopPropagation()}
          style={{ position:'absolute', top:'calc(100% + 6px)', right:0, zIndex:60, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', boxShadow:'var(--shadow-lg)', padding:'10px', minWidth:'180px' }}>
          <div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px', fontFamily:'var(--font-ui)' }}>Show columns</div>
          <div style={{ display:'flex', flexDirection:'column', gap:'4px' }}>
            {ALL_COLUMNS.map(c=>(
              <label key={c.id} style={{ display:'flex', alignItems:'center', gap:'8px', padding:'4px 6px', borderRadius:'var(--radius-sm)', cursor:c.locked?'default':'pointer', opacity:c.locked?0.5:1 }}>
                <input type="checkbox" checked={!!visible[c.id]} disabled={c.locked}
                  onChange={e=>setVisible(v=>({...v,[c.id]:e.target.checked}))}
                  style={{ accentColor:'var(--primary)', cursor:c.locked?'default':'pointer' }}/>
                <span style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{c.label}{c.locked?' (always shown)':''}</span>
              </label>
            ))}
          </div>
          <button onClick={()=>setVisible(DEFAULT_VISIBLE)}
            style={{ marginTop:'8px', width:'100%', padding:'7px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
            Reset to default
          </button>
        </div>
      )}
    </div>
  )
}

// ── Action button ─────────────────────────────────────────────────────────────
function ActionBtn({ onClick, title, iconPath, hoverBg, hoverColor }) {
  const [h, setH] = useState(false)
  return (
    <button onClick={onClick} title={title}
      style={{ width:'28px', height:'28px', display:'flex', alignItems:'center', justifyContent:'center', borderRadius:'var(--radius-sm)', border:'none', cursor:'pointer', background:h?hoverBg:'transparent', transition:'var(--transition-fast)' }}
      onMouseEnter={()=>setH(true)} onMouseLeave={()=>setH(false)}>
      <Icon path={iconPath} size={13} color={h?hoverColor:'var(--text-muted)'} sw={2}/>
    </button>
  )
}

// ── Import modal ──────────────────────────────────────────────────────────────
function ImportModal({ onClose, onImport, t }) {
  const [file, setFile]       = useState(null)
  const [preview, setPreview] = useState([])
  const fileRef = useRef()

  const handleFile = (f) => {
    setFile(f)
    const reader = new FileReader()
    reader.onload = (e) => { setPreview(e.target.result.split('\n').filter(Boolean).slice(0,4)) }
    reader.readAsText(f)
  }

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.6)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:300, padding:'16px' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="animate-scale-in" style={{ background:'var(--bg-card)', borderRadius:'var(--radius-xl)', padding:'24px', width:'100%', maxWidth:'480px', fontFamily:'var(--font-ui)', border:'1px solid var(--border)', boxShadow:'var(--shadow-xl)' }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'16px' }}>
          <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)' }}>{t('orders.import_title')}</div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'18px' }}>×</button>
        </div>
        <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'14px', lineHeight:1.6, fontFamily:'var(--font-body)' }}>{t('orders.import_desc')}</div>
        <div onClick={()=>fileRef.current?.click()}
          style={{ border:'2px dashed var(--border)', borderRadius:'var(--radius-lg)', padding:'28px', textAlign:'center', cursor:'pointer', background:'var(--bg-muted)', marginBottom:'14px', transition:'border-color 0.15s' }}
          onMouseEnter={e=>e.currentTarget.style.borderColor='var(--border-focus)'}
          onMouseLeave={e=>e.currentTarget.style.borderColor='var(--border)'}>
          <Icon path={IC.upload} color='var(--primary)' size={28} sw={1.5}/>
          <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', marginTop:'8px' }}>
            {file ? file.name : t('orders.click_to_select')}
          </div>
          <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'3px' }}>{t('orders.csv_only')}</div>
          <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e=>e.target.files[0]&&handleFile(e.target.files[0])}/>
        </div>
        {preview.length>0&&(
          <div style={{ background:'var(--bg-muted)', borderRadius:'var(--radius-md)', padding:'10px', marginBottom:'14px', overflowX:'auto' }}>
            {preview.map((line,i)=>(
              <div key={i} style={{ fontSize:'10.5px', color:i===0?'var(--primary)':'var(--text-primary)', fontFamily:'var(--font-mono)', marginBottom:'3px', whiteSpace:'nowrap' }}>
                {line.slice(0,80)}{line.length>80?'...':''}
              </div>
            ))}
          </div>
        )}
        <div style={{ display:'flex', gap:'10px' }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'13px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>{t('orders.cancel')}</button>
          <button onClick={()=>file&&onImport(file)} disabled={!file}
            style={{ flex:1, padding:'11px', borderRadius:'var(--radius-md)', background:file?'var(--primary)':'var(--border)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:file?'pointer':'not-allowed', fontFamily:'var(--font-ui)' }}>
            {t('orders.import_btn')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Tara Order Card — multi-item cart submission from order form ──────────────
function TaraOrderCard({ group, onConfirm, onReject, processingId, sym, expandedId, setExpandedId }) {
  const isProcessing = processingId === group.id
  const isExpanded    = expandedId === group.id
  const first = group.items[0]

  return (
    <div className="pc-card" style={{ overflow:'hidden', border:'1.5px solid rgba(45,212,176,0.3)' }}>
      {/* Header */}
      <div style={{ padding:'14px 18px', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'space-between', gap:'12px', flexWrap:'wrap' }}>
        <div style={{ display:'flex', alignItems:'center', gap:'10px', minWidth:0 }}>
          <div style={{ width:'32px', height:'32px', borderRadius:'50%', background:'rgba(255,255,255,0.1)', border:'1.5px solid rgba(45,212,176,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
            <TaraStarIcon size={16} />
          </div>
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'13px', fontWeight:700, color:'white', fontFamily:'var(--font-ui)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{first.customer_name||'Unknown customer'}</div>
            <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.65)', marginTop:'2px', fontFamily:'var(--font-body)' }}>{first.customer_phone}</div>
          </div>
        </div>
        <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.5)', textAlign:'right', fontFamily:'var(--font-body)', flexShrink:0 }}>
          {first.created_at?new Date(first.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})+' '+new Date(first.created_at).toLocaleTimeString('en-BD',{hour:'2-digit',minute:'2-digit'}):'Just now'}
        </div>
      </div>

      <div style={{ padding:'16px 18px' }}>
        {/* Items */}
        <div style={{ marginBottom:'12px' }}>
          <div style={{ fontSize:'11px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'8px', fontFamily:'var(--font-ui)' }}>
            {group.items.length} item{group.items.length!==1?'s':''}
          </div>
          {group.items.map((it,i)=>(
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:i<group.items.length-1?'1px solid var(--border)':'none' }}>
              <div style={{ minWidth:0 }}>
                <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>{it.product_name||'Unknown product'}</div>
                <div style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>
                  {it.variant_name?it.variant_name+' · ':''}Qty {it.quantity}
                  {it.product_code&&<span style={{ fontFamily:'var(--font-mono)' }}> · {it.product_code}</span>}
                </div>
              </div>
              <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', flexShrink:0 }}>
                {sym}{(Number(it.unit_price||0)*Number(it.quantity||1)).toLocaleString()}
              </div>
            </div>
          ))}
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', paddingTop:'10px', marginTop:'4px', borderTop:'1px solid var(--border)' }}>
            <span style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-ui)' }}>Total</span>
            <span style={{ fontSize:'16px', fontWeight:700, color:'#0D9488', fontFamily:'var(--font-ui)' }}>{sym}{group.total.toLocaleString()}</span>
          </div>
        </div>

        {/* Delivery type */}
        {first.delivery_type && (
          <div style={{ display:'flex', alignItems:'center', gap:'6px', marginBottom:'10px', fontSize:'12px', color:'var(--text-muted)' }}>
            {first.delivery_type==='pickup' ? '🏪' : first.delivery_type==='express' ? '⚡' : '📦'}
            <span style={{ textTransform:'capitalize' }}>{first.delivery_type} delivery</span>
          </div>
        )}

        {/* Address */}
        {first.customer_address && (
          <div style={{ marginBottom:'10px' }}>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'4px', fontFamily:'var(--font-body)' }}>Delivery address</div>
            <div style={{ fontSize:'12.5px', fontWeight:500, color:'var(--text-primary)', lineHeight:1.6, fontFamily:'var(--font-body)' }}>{first.customer_address}</div>
          </div>
        )}

        {/* Payment method + proof */}
        {first.payment_method && (
          <div style={{ marginBottom:'10px', padding:'10px 12px', borderRadius:'var(--radius-md)', background:'var(--bg-muted)', border:'1px solid var(--border)' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'6px' }}>
              <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                <span style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>Payment:</span>
                <span style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', textTransform:'capitalize' }}>{first.payment_method.replace('_',' ')}</span>
              </div>
              {first.transaction_id && (
                <span style={{ fontSize:'11px', fontFamily:'var(--font-mono)', color:'var(--primary)', background:'var(--primary-light)', padding:'2px 8px', borderRadius:'var(--radius-full)' }}>
                  {first.transaction_id}
                </span>
              )}
            </div>
            {first.payment_screenshot_url && (
              <a href={first.payment_screenshot_url} target="_blank" rel="noreferrer"
                style={{ display:'inline-flex', alignItems:'center', gap:'5px', marginTop:'8px', fontSize:'11.5px', fontWeight:600, color:'var(--primary)', textDecoration:'none' }}>
                <Icon path={IC.image} size={13} color="var(--primary)" sw={2}/> View payment screenshot
              </a>
            )}
          </div>
        )}

        {first.customer_note && (
          <div style={{ marginBottom:'10px' }}>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'4px', fontFamily:'var(--font-body)' }}>Note</div>
            <div style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{first.customer_note}</div>
          </div>
        )}

        <button onClick={()=>setExpandedId(isExpanded?null:group.id)}
          style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', padding:0, fontFamily:'var(--font-ui)' }}>
          {isExpanded?'Hide raw details':'View raw details'}
        </button>
        {isExpanded&&(
          <div style={{ marginTop:'10px', borderRadius:'var(--radius-md)', padding:'12px', background:'var(--bg-muted)', border:'1px solid var(--border)' }}>
            {[
              {label:'District',value:first.customer_district},
              {label:'Source',value:first.source},
              {label:'Status',value:first.status},
            ].filter(f=>f.value).map((f,i)=>(
              <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'4px', fontSize:'12px', fontFamily:'var(--font-body)' }}>
                <span style={{ color:'var(--text-muted)', flexShrink:0, width:'90px' }}>{f.label}:</span>
                <span style={{ color:'var(--text-primary)' }}>{f.value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ padding:'0 18px 18px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
        <button onClick={()=>onReject(group)} disabled={isProcessing}
          style={{ padding:'10px', borderRadius:'var(--radius-md)', fontSize:'12.5px', fontWeight:600, border:'1px solid var(--error-border)', background:'var(--error-light)', color:'var(--error)', cursor:'pointer', opacity:isProcessing?0.5:1, fontFamily:'var(--font-ui)' }}>
          {isProcessing?'...':'Reject'}
        </button>
        <button onClick={()=>onConfirm(group)} disabled={isProcessing}
          style={{ padding:'10px', borderRadius:'var(--radius-md)', fontSize:'12.5px', fontWeight:600, border:'none', background:isProcessing?'var(--border)':'#0D9488', color:'white', cursor:'pointer', opacity:isProcessing?0.5:1, fontFamily:'var(--font-ui)', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
          {isProcessing ? 'Confirming...' : <><TaraStarIcon size={12} color="white" /> Confirm Order</>}
        </button>
      </div>
    </div>
  )
}

// ── Main Orders component ─────────────────────────────────────────────────────
function Orders() {
  const navigate   = useNavigate()
  const location   = useLocation()
  const { seller } = useAuth()
  const { showToast } = useToast()
  const { t } = useTranslation()

  const currency = seller?.seller_currency || 'BDT'
  const sym      = getCurrencySymbol(currency)

  const [orders, setOrders]                   = useState([])
  const [pendingRequests, setPendingRequests]  = useState([])
  const [taraSubmissions, setTaraSubmissions]  = useState([])
  const [loading, setLoading]                 = useState(true)
  const [loadingRequests, setLoadingRequests] = useState(true)
  const [loadingTara, setLoadingTara]         = useState(true)
  const [search, setSearch]                   = useState('')
  const [filterStatus, setFilterStatus]       = useState('all')
  const [activeTab, setActiveTab]             = useState('orders')
  const [processingId, setProcessingId]       = useState(null)
  const [expandedRequest, setExpandedRequest] = useState(null)
  const [expandedTara, setExpandedTara]       = useState(null)
  const [currentPage, setCurrentPage]         = useState(1)
  const [showImport, setShowImport]           = useState(false)
  const [showCourierExport, setShowCourierExport] = useState(false)
  const [dailyData, setDailyData]             = useState([])
  const [statusData, setStatusData]           = useState([])
  const [fulfillingId, setFulfillingId]       = useState(null)
  const [dateFrom, setDateFrom]               = useState('')
  const [dateTo, setDateTo]                   = useState('')
  const [showDateFilter, setShowDateFilter]   = useState(false)
  const [hoveredRow, setHoveredRow]           = useState(null)
  const [openFilter, setOpenFilter]           = useState(null) // which column filter popover is open
  const [colFilters, setColFilters]           = useState({ customer:'', product:'', location:'', amountMin:'', amountMax:'' })
  const [columnsOpen, setColumnsOpen]         = useState(false)
  const [visibleCols, setVisibleCols]         = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      if (saved) return { ...DEFAULT_VISIBLE, ...JSON.parse(saved) }
    } catch {}
    return DEFAULT_VISIBLE
  })

  useEffect(() => { if (location.state?.tab) setActiveTab(location.state.tab) }, [location.state])
  useEffect(() => { if (seller?.id) { fetchOrders(); fetchPendingRequests(); fetchTaraSubmissions() } }, [seller]) // eslint-disable-line
  useEffect(() => { setCurrentPage(1) }, [search, filterStatus, dateFrom, dateTo, activeTab, colFilters])
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols)) } catch {}
  }, [visibleCols])
  useEffect(() => {
    if (!openFilter && !columnsOpen) return
    const handler = () => { setOpenFilter(null); setColumnsOpen(false) }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [openFilter, columnsOpen])

  const fetchOrders = async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('orders')
      .select('*, customers(name,phone,thana,district,metro_thana,neighbourhood,moholla,union_name)')
      .eq('seller_id', seller?.id)
      .order('created_at', { ascending:false })
    if (!error) { const arr=data||[]; setOrders(arr); buildCharts(arr) }
    setLoading(false)
  }

  const buildCharts = (arr) => {
    const dayMap = {}
    for (let i=6;i>=0;i--) {
      const d=new Date(); d.setDate(d.getDate()-i)
      const key=d.toLocaleDateString('en-BD',{month:'short',day:'numeric'})
      dayMap[key]=0
    }
    arr.filter(o=>o.status!=='pre_order').forEach(o=>{
      const key=new Date(o.created_at).toLocaleDateString('en-BD',{month:'short',day:'numeric'})
      if(key in dayMap) dayMap[key]++
    })
    setDailyData(Object.entries(dayMap).map(([day,count])=>({day,count})))
    const sc={}
    arr.filter(o=>o.status!=='pre_order').forEach(o=>{ sc[o.status]=(sc[o.status]||0)+1 })
    setStatusData(Object.entries(sc).map(([status,value])=>({ status,value,label:STATUS_CONFIG[status]?.label||status,color:STATUS_CONFIG[status]?.pillText||'var(--text-muted)' })))
  }

  const fetchPendingRequests = async () => {
    setLoadingRequests(true)
    const { data } = await supabase.from('customer_order_requests').select('*').eq('seller_id',seller?.id).eq('status','submitted').order('submitted_at',{ascending:false})
    setPendingRequests(data||[])
    setLoadingRequests(false)
  }

  const fetchTaraSubmissions = async () => {
    setLoadingTara(true)
    const { data } = await supabase.from('seller_order_form_submissions').select('*').eq('seller_id',seller?.id).eq('status','pending').order('created_at',{ascending:false})
    setTaraSubmissions(data||[])
    setLoadingTara(false)
  }

  // Group Tara submissions by customer+phone+timestamp-window so multi-item carts
  // (inserted as separate rows, one per cart item) appear as a single order card
  const taraGroups = (() => {
    const groups = {}
    taraSubmissions.forEach(sub => {
      // Group key: same phone + submitted within the same minute = same cart
      const minuteKey = sub.created_at ? sub.created_at.slice(0,16) : 'unknown'
      const key = `${sub.customer_phone}__${minuteKey}`
      if (!groups[key]) groups[key] = { id:key, items:[], total:0 }
      groups[key].items.push(sub)
      groups[key].total += Number(sub.unit_price||0) * Number(sub.quantity||1)
    })
    return Object.values(groups).sort((a,b) => new Date(b.items[0].created_at) - new Date(a.items[0].created_at))
  })()

  const handleFulfilPreorder = async (order) => {
    setFulfillingId(order.id)
    try {
      await supabase.from('orders').update({ status:'confirmed', is_preorder:true }).eq('id',order.id)
      await supabase.from('order_status_log').insert({ order_id:order.id, old_status:'pre_order', new_status:'confirmed', note:'Pre-order fulfilled — stock deducted', changed_by:'Owner' })
      if (order.product_id) {
        if (order.variant_id) {
          const { data:v } = await supabase.from('product_variants').select('stock').eq('id',order.variant_id).single()
          if (v) await supabase.from('product_variants').update({ stock:Math.max(0,(v.stock||0)-(order.quantity||1)) }).eq('id',order.variant_id)
        } else {
          const { data:p } = await supabase.from('products').select('stock').eq('id',order.product_id).single()
          if (p) await supabase.from('products').update({ stock:Math.max(0,(p.stock||0)-(order.quantity||1)) }).eq('id',order.product_id)
        }
        await supabase.from('stock_log').insert({ product_id:order.product_id, action:'deducted', quantity:order.quantity||1, note:'Pre-order '+order.order_code+' fulfilled', done_by:'Owner' })
      }
      await fetchOrders()
      showToast('Pre-order fulfilled! Stock deducted.')
      setTimeout(()=>navigate('/orders/'+order.id),600)
    } catch(e) { showToast('Error: '+e.message,'error') }
    setFulfillingId(null)
  }

  const handleApprove = async (req) => {
    setProcessingId(req.id)
    try {
      let customerId = null
      const fullPhone = (req.customer_country_code||'+880')+(req.customer_phone||'')

      // Optional profile fields from the customer order form — only included if filled in
      const profilePatch = {}
      ;['birthday','anniversary','gender','age_range','whatsapp','email','facebook_url','instagram_url'].forEach(k => {
        if (req[k]) profilePatch[k] = req[k]
      })

      // Phone is a lookup aid, not a unique key — different people can share a number,
      // so just take the first match if one exists rather than .single() (which errors on 2+ rows)
      const { data:existingRows } = await supabase.from('customers').select('id').eq('phone',req.customer_phone||fullPhone).eq('seller_id',seller.id).limit(1)
      const existing = existingRows?.[0]

      if (existing) {
        customerId = existing.id
        if (Object.keys(profilePatch).length > 0) {
          await supabase.from('customers').update(profilePatch).eq('id', customerId)
        }
      } else {
        const { data:nc } = await supabase.from('customers').insert({ seller_id:seller.id, name:req.customer_name, phone:req.customer_phone||fullPhone, country_code:req.customer_country_code||'+880', house:req.house||null, road:req.road||null, moholla:req.moholla||null, thana:req.thana||null, thana_id:req.thana_id||null, district:req.district||null, district_id:req.district_id||null, division:req.division||null, division_id:req.division_id||null, union_name:req.union_name||null, union_id:req.union_id||null, zip:req.zip||null, country:req.country||'Bangladesh', extra:req.extra||null, metro_thana:req.metro_thana||null, neighbourhood:req.neighbourhood||null, ...profilePatch }).select().single()
        customerId = nc?.id
      }
      const orderCode = 'ORD-'+new Date().getFullYear()+'-'+Math.floor(1000+Math.random()*9000)
      let productId=null; let productName=null; let productPrice=null
      if (req.product_code) {
        const { data:fp } = await supabase.from('products').select('id,name,sell_price').eq('seller_id',seller.id).ilike('product_code',req.product_code.trim()).limit(1)
        if (fp?.length>0) { productId=fp[0].id; productName=fp[0].name; productPrice=fp[0].sell_price }
      }
      await supabase.from('orders').insert({ seller_id:seller.id, customer_id:customerId, order_code:orderCode, product_id:productId, product_code:req.product_code||null, product_name:productName||null, unit_price:productPrice||0, total_amount:productPrice||0, order_source:'Facebook inbox', submitted_by:'Customer (order form)', notes:req.notes||null, status:'received', payment_status:'Unpaid COD', delivery_charge:0, quantity:1, amount_paid:0 })
      await supabase.from('customer_order_requests').update({ status:'approved', reviewed_at:new Date().toISOString() }).eq('id',req.id)
      await fetchPendingRequests()
      const { data:newOrders } = await supabase.from('orders').select('id').eq('seller_id',seller.id).eq('submitted_by','Customer (order form)').order('created_at',{ascending:false}).limit(1)
      setProcessingId(null)
      showToast(t('orders.approved_toast'))
      if (newOrders?.[0]?.id) setTimeout(()=>navigate('/orders/'+newOrders[0].id),500)
      else await fetchOrders()
    } catch { showToast(t('orders.approve_error'),'error'); setProcessingId(null) }
  }

  const handleDecline = async (req) => {
    setProcessingId(req.id)
    await supabase.from('customer_order_requests').update({ status:'declined', reviewed_at:new Date().toISOString() }).eq('id',req.id)
    await fetchPendingRequests()
    showToast(t('orders.declined_toast'),'info')
    setProcessingId(null)
  }

  // ── Tara order confirm/reject — handles multi-item cart groups ────────────
  const handleConfirmTaraOrder = async (group) => {
    setProcessingId(group.id)
    try {
      const first = group.items[0]
      const fullPhone = first.customer_phone

      // Find or create customer
      const { data:existingRows } = await supabase.from('customers').select('id').eq('phone',fullPhone).eq('seller_id',seller.id).limit(1)
      let customerId = existingRows?.[0]?.id

      if (!customerId) {
        const { data:nc } = await supabase.from('customers').insert({
          seller_id: seller.id, name: first.customer_name, phone: fullPhone,
          district: first.customer_district || null,
        }).select().single()
        customerId = nc?.id
      }

      const orderCode = 'ORD-'+new Date().getFullYear()+'-'+Math.floor(1000+Math.random()*9000)

      // Look up product for first item to set legacy single-product fields
      let firstProductId = null
      if (first.product_code) {
        const { data:fp } = await supabase.from('products').select('id').eq('seller_id',seller.id).ilike('product_code',first.product_code.trim()).limit(1)
        if (fp?.length>0) firstProductId = fp[0].id
      }

      const { data:newOrder, error:orderError } = await supabase.from('orders').insert({
        seller_id: seller.id, customer_id: customerId, order_code: orderCode,
        product_id: firstProductId, product_code: first.product_code || null,
        product_name: first.product_name || null, variant_name: first.variant_name || null,
        quantity: first.quantity || 1, unit_price: first.unit_price || 0,
        total_amount: group.total, items_subtotal: group.total, has_multiple_items: group.items.length > 1,
        order_source: 'Customer order form', submitted_by: 'Tara — Quick Order Form',
        notes: first.customer_note || null, status: 'received',
        payment_method: first.payment_method || null,
        payment_status: first.payment_method === 'cod' ? 'Unpaid COD' : 'Partial payment',
        delivery_charge: 0, amount_paid: 0,
      }).select().single()

      if (orderError) throw orderError

      // Insert order_items for ALL cart items
      if (group.items.length > 0) {
        const itemRows = group.items.map(it => ({
          order_id: newOrder.id, seller_id: seller.id,
          product_code: it.product_code, product_name: it.product_name,
          variant_name: it.variant_name || null,
          quantity: Number(it.quantity || 1), unit_price: Number(it.unit_price || 0),
          line_subtotal: Number(it.unit_price || 0) * Number(it.quantity || 1),
        }))
        await supabase.from('order_items').insert(itemRows)
      }

      // Mark all submission rows in this group as converted
      const ids = group.items.map(it => it.id)
      await supabase.from('seller_order_form_submissions').update({ status:'converted', converted_order_id: newOrder.id }).in('id', ids)

      await fetchTaraSubmissions()
      setProcessingId(null)
      showToast('Order confirmed!')
      setTimeout(()=>navigate('/orders/'+newOrder.id), 500)
    } catch(e) {
      showToast('Error: '+e.message, 'error')
      setProcessingId(null)
    }
  }

  const handleRejectTaraOrder = async (group) => {
    setProcessingId(group.id)
    const ids = group.items.map(it => it.id)
    await supabase.from('seller_order_form_submissions').update({ status:'rejected' }).in('id', ids)
    await fetchTaraSubmissions()
    showToast('Order rejected', 'info')
    setProcessingId(null)
  }

  const handleImport = () => { setShowImport(false); showToast(t('orders.imported_toast')) }

  const getLocationLines = (customer) => {
    const line1 = [customer?.neighbourhood,customer?.metro_thana,customer?.moholla,customer?.union_name,customer?.thana].filter(v=>v&&v!=='N/A'&&v!=='__na__'&&v!=='__other__').slice(0,1)[0]||''
    const line2 = customer?.district&&customer.district!=='N/A'?customer.district:''
    return { line1, line2 }
  }

  const formatAddress = (req) => [
    req.house&&req.house!=='N/A'?'House: '+req.house:null,
    req.moholla&&req.moholla!=='N/A'?req.moholla:null,
    req.road&&req.road!=='N/A'?'Road: '+req.road:null,
    req.neighbourhood&&req.neighbourhood!=='N/A'?req.neighbourhood:null,
    req.metro_thana&&req.metro_thana!=='N/A'?req.metro_thana:null,
    req.union_name&&req.union_name!=='N/A'?req.union_name:null,
    req.zip||null,
    req.thana&&req.thana!=='N/A'?req.thana:null,
    req.district&&req.district!=='N/A'?req.district:null,
    req.division&&req.division!=='N/A'?req.division:null,
    req.country||null,
  ].filter(Boolean).join(', ')

  const resetDateFilter = () => { setDateFrom(''); setDateTo(''); setShowDateFilter(false) }
  const hasDateFilter = dateFrom||dateTo

  const preOrders = orders.filter(o=>o.status==='pre_order')
  const mainOrders = orders.filter(o=>o.status!=='pre_order')

  const filtered = mainOrders.filter(o => {
    const q = search.toLowerCase()
    const matchSearch = !search||o.customers?.name?.toLowerCase().includes(q)||o.order_code?.toLowerCase().includes(q)||o.customers?.phone?.includes(search)||o.product_name?.toLowerCase().includes(q)||o.product_code?.toLowerCase().includes(q)
    const matchStatus = filterStatus==='all'||o.status===filterStatus
    let matchDate = true
    if (dateFrom) matchDate = matchDate&&new Date(o.created_at)>=new Date(dateFrom)
    if (dateTo) { const to=new Date(dateTo); to.setHours(23,59,59); matchDate=matchDate&&new Date(o.created_at)<=to }
    // Column filters
    const matchCustomer = !colFilters.customer || o.customers?.name?.toLowerCase().includes(colFilters.customer.toLowerCase())
    const matchProduct  = !colFilters.product || o.product_name?.toLowerCase().includes(colFilters.product.toLowerCase())
    const loc = getLocationLines(o.customers)
    const locStr = (loc.line1+' '+loc.line2).toLowerCase()
    const matchLocation = !colFilters.location || locStr.includes(colFilters.location.toLowerCase())
    const amt = Number(o.total_amount||0)
    const matchAmountMin = !colFilters.amountMin || amt >= Number(colFilters.amountMin)
    const matchAmountMax = !colFilters.amountMax || amt <= Number(colFilters.amountMax)
    return matchSearch&&matchStatus&&matchDate&&matchCustomer&&matchProduct&&matchLocation&&matchAmountMin&&matchAmountMax
  })

  const totalPages = Math.ceil(filtered.length/ORDERS_PER_PAGE)
  const pageStart  = (currentPage-1)*ORDERS_PER_PAGE
  const pageEnd    = Math.min(pageStart+ORDERS_PER_PAGE, filtered.length)
  const pageOrders = filtered.slice(pageStart, pageEnd)

  const filteredPreorders = preOrders.filter(o => {
    const q=search.toLowerCase()
    return !search||o.customers?.name?.toLowerCase().includes(q)||o.order_code?.toLowerCase().includes(q)||o.customers?.phone?.includes(search)||o.product_name?.toLowerCase().includes(q)
  })

  const fmt = (n) => {
    const abs=Math.abs(Math.round(n||0))
    if(abs>=100000) return sym+Math.round(abs/1000)+'k'
    return sym+abs.toLocaleString()
  }

  const totalPaid    = mainOrders.filter(o=>o.payment_status==='Paid in full').length
  const totalPending = mainOrders.filter(o=>o.status==='pending'||o.status==='received').length
  const totalOverdue = mainOrders.filter(o=>o.payment_status==='Unpaid COD'&&o.status==='shipped').length
  const preOrderPipelineValue = preOrders.reduce((s,o)=>s+Number(o.total_amount||0),0)

  const PREORDER_COLS = '36px 110px 1fr 1fr 120px 85px 95px 90px 120px'
  const visibleColumns = ALL_COLUMNS.filter(c=>visibleCols[c.id])
  const TABLE_COLS = visibleColumns.map(c=>c.width).join(' ')
  const TABLE_GAP = 10
  const FR_MIN_PX = 180 // minimum width per flexible (1fr) column
  const TABLE_MIN_WIDTH = visibleColumns.reduce((sum,c)=>{
    return sum + (c.width.endsWith('fr') ? FR_MIN_PX : parseInt(c.width))
  }, 0) + (visibleColumns.length-1)*TABLE_GAP + 36 // + horizontal padding
  const PIE_COLORS = ['#0EA5E9','#2DD4B0','#F59E0B','#7C3AED','#EF4444','#10B981','#64748B','#F97316']

  // Identifies an order as Tara-sourced — checked against submitted_by / order_source
  const isTaraOrder = (order) => {
    return order.submitted_by?.toLowerCase().includes('tara') ||
           order.order_source === 'Customer order form'
  }

  const renderOrderRow = (order, slNum, isPreorderTab=false) => {
    const ss  = STATUS_CONFIG[order.status]||{ bg:'var(--bg-muted)', text:'var(--text-muted)', label:order.status }
    const loc = getLocationLines(order.customers)
    const grandTotal = Number(order.total_amount||0)
    const vat = Number(order.vat_amount||0)
    const payShort = order.payment_status==='Paid in full'?'Paid':order.payment_status==='Unpaid COD'?'COD':order.payment_status==='50% advance'?'50% Adv':order.payment_status==='Partial payment'?'Partial':order.payment_status||'—'
    const payColor = order.payment_status==='Paid in full'?'var(--success)':'var(--warning)'
    const isFulfilling = fulfillingId===order.id
    const isHovered = hoveredRow===order.id
    const taraSourced = isTaraOrder(order)

    return (
      <div key={order.id} style={{ borderBottom:'1px solid var(--border)' }}>
        {/* Desktop row */}
        <div className="orders-row"
          onClick={()=>navigate('/orders/'+order.id)}
          onMouseEnter={()=>setHoveredRow(order.id)}
          onMouseLeave={()=>setHoveredRow(null)}
          style={{ display:'grid', gridTemplateColumns:isPreorderTab?PREORDER_COLS:TABLE_COLS, gap:TABLE_GAP+'px', padding:'12px 18px', alignItems:'center', cursor:'pointer', background:isHovered?'var(--bg-muted)':'var(--bg-card)', transition:'background 0.1s' }}>

          {/* SL */}
          {(isPreorderTab||visibleCols.sl)&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{slNum}</div>}

          {/* Order ID + source */}
          {(isPreorderTab||visibleCols.orderId)&&(
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'12px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)', marginBottom:'4px' }}>{order.order_code}</div>
            <div style={{ display:'flex', gap:'4px', flexWrap:'wrap' }}>
              {taraSourced ? <TaraBadge /> : <SourceBadge orderSource={order.order_source}/>}
              {order.is_preorder&&order.status!=='pre_order'&&(
                <span style={{ fontSize:'9.5px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'rgba(13,148,136,0.12)', color:'#0D9488', fontFamily:'var(--font-ui)' }}>Pre-order</span>
              )}
            </div>
          </div>
          )}

          {/* Customer */}
          {(isPreorderTab||visibleCols.customer)&&(
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{order.customers?.name||'—'}</div>
            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{order.customers?.phone||'—'}</div>
          </div>
          )}

          {/* Product */}
          {(isPreorderTab||visibleCols.product)&&(
          <div style={{ minWidth:0 }}>
            <div style={{ fontSize:'12.5px', fontWeight:500, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'2px', fontFamily:'var(--font-body)' }}>
              {order.product_name||'—'}{order.has_multiple_items ? ' +more' : ''}
            </div>
            {order.product_code&&<div style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-mono)' }}>{order.product_code}</div>}
          </div>
          )}

          {/* Location */}
          {(isPreorderTab||visibleCols.location)&&(
          <div style={{ minWidth:0 }}>
            {loc.line1&&<div style={{ fontSize:'11.5px', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{loc.line1}</div>}
            {loc.line2&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{loc.line2}</div>}
            {!loc.line1&&!loc.line2&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>—</div>}
          </div>
          )}

          {/* Date */}
          {(isPreorderTab||visibleCols.date)&&(
          <div>
            <div style={{ fontSize:'12px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{new Date(order.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short',year:'2-digit'})}</div>
            {order.expected_delivery&&<div style={{ fontSize:'10.5px', color:'#0D9488', fontWeight:600, marginTop:'2px', fontFamily:'var(--font-body)' }}>ETA {new Date(order.expected_delivery).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>}
          </div>
          )}

          {/* Amount */}
          {(isPreorderTab||visibleCols.amount)&&(
          <div>
            <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{fmt(grandTotal)}</div>
            {vat>0&&<div style={{ fontSize:'10.5px', color:'var(--text-muted)', marginBottom:'1px', fontFamily:'var(--font-body)' }}>+{fmt(vat)} VAT</div>}
            <div style={{ fontSize:'11px', fontWeight:600, color:payColor, fontFamily:'var(--font-body)' }}>{payShort}</div>
          </div>
          )}

          {/* Status column — always its own cell */}
          {isPreorderTab ? (
            <div/>
          ) : visibleCols.status ? (
            <div>
              <span style={{ fontSize:'11px', fontWeight:600, padding:'3px 10px', borderRadius:'var(--radius-full)', background:ss.bg, color:ss.text, fontFamily:'var(--font-ui)', whiteSpace:'nowrap' }}>
                {ss.label}
              </span>
            </div>
          ) : null}

          {/* Actions column — always visible, dimmed until row hover */}
          {isPreorderTab ? (
            <div style={{ display:'flex', gap:'6px', alignItems:'center' }} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>handleFulfilPreorder(order)} disabled={isFulfilling}
                style={{ padding:'6px 14px', borderRadius:'var(--radius-md)', background:isFulfilling?'var(--border)':'#0D9488', border:'none', fontSize:'12px', fontWeight:700, color:'white', cursor:isFulfilling?'not-allowed':'pointer', display:'flex', alignItems:'center', gap:'5px', fontFamily:'var(--font-ui)' }}>
                <Icon path={IC.check2} color='white' size={11} sw={2.5}/>
                {isFulfilling?'Fulfilling...':'Fulfil'}
              </button>
              <ActionBtn onClick={()=>navigate('/orders/'+order.id)} title="View" iconPath={IC.eye} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
            </div>
          ) : visibleCols.actions ? (
            <div style={{ display:'flex', alignItems:'center', gap:'2px', opacity:isHovered?1:0.3, transition:'opacity 0.15s' }} onClick={e=>e.stopPropagation()}>
              <ActionBtn onClick={()=>navigate('/orders/'+order.id)} title={t('orders.col_actions')} iconPath={IC.eye} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
              <ActionBtn onClick={()=>navigate('/orders/'+order.id+'?edit=1')} title={t('common.edit')} iconPath={IC.edit} hoverBg='var(--primary-light)' hoverColor='var(--primary)'/>
              <ActionBtn onClick={()=>navigate('/orders/'+order.id+'?receipt=1')} title={t('common.download')} iconPath={IC.download} hoverBg='var(--success-light)' hoverColor='var(--success)'/>
              <ActionBtn onClick={()=>window.print()} title={t('common.print')} iconPath={IC.print} hoverBg='#F5F3FF' hoverColor='#6D28D9'/>
            </div>
          ) : null}
        </div>

        {/* Mobile card */}
        <div className="orders-mobile-card" onClick={()=>navigate('/orders/'+order.id)}
          style={{ display:'none', padding:'14px 16px', cursor:'pointer' }}
          onMouseEnter={e=>e.currentTarget.style.background='var(--bg-muted)'}
          onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>

          {/* Row 1: SL + Order ID + source badges ... status pill */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'8px', gap:'8px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'8px', flexWrap:'wrap', minWidth:0 }}>
              <span style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', flexShrink:0 }}>#{slNum}</span>
              <span style={{ fontSize:'12px', fontWeight:700, color:'var(--primary)', fontFamily:'var(--font-mono)' }}>{order.order_code}</span>
              {order.is_preorder&&order.status!=='pre_order'&&<span style={{ fontSize:'9.5px', fontWeight:700, padding:'1px 6px', borderRadius:'var(--radius-full)', background:'rgba(13,148,136,0.12)', color:'#0D9488', flexShrink:0 }}>Pre-order</span>}
              {taraSourced ? <TaraBadge /> : <SourceBadge orderSource={order.order_source}/>}
            </div>
            {!isPreorderTab&&<span style={{ fontSize:'11px', fontWeight:600, padding:'2px 8px', borderRadius:'var(--radius-full)', background:ss.bg, color:ss.text, flexShrink:0, whiteSpace:'nowrap' }}>{ss.label}</span>}
          </div>

          {/* Row 2: Customer + Product */}
          <div style={{ marginBottom:'8px' }}>
            <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{order.customers?.name||'—'}</div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'2px', fontFamily:'var(--font-body)' }}>{order.customers?.phone||'—'}</div>
            <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{order.product_name}{order.has_multiple_items?' +more':''}{order.product_code?' · '+order.product_code:''}</div>
          </div>

          {/* Row 3: Location + Date (two columns) */}
          <div style={{ display:'flex', gap:'12px', marginBottom:'10px', paddingTop:'8px', borderTop:'1px solid var(--border)' }}>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Location</div>
              {loc.line1&&<div style={{ fontSize:'12px', color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{loc.line1}</div>}
              {loc.line2&&<div style={{ fontSize:'11.5px', color:'var(--text-muted)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontFamily:'var(--font-body)' }}>{loc.line2}</div>}
              {!loc.line1&&!loc.line2&&<div style={{ fontSize:'12px', color:'var(--text-muted)' }}>—</div>}
            </div>
            <div style={{ flexShrink:0, textAlign:'right' }}>
              <div style={{ fontSize:'10px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.05em', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Date</div>
              <div style={{ fontSize:'12px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{new Date(order.created_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>
              {order.expected_delivery&&<div style={{ fontSize:'10.5px', color:'#0D9488', fontWeight:600, fontFamily:'var(--font-body)' }}>ETA {new Date(order.expected_delivery).toLocaleDateString('en-BD',{day:'numeric',month:'short'})}</div>}
            </div>
          </div>

          {/* Row 4: Amount + payment status */}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'10px' }}>
            <div>
              <div style={{ fontSize:'15px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{fmt(grandTotal)}</div>
              {vat>0&&<div style={{ fontSize:'10.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>+{fmt(vat)} VAT</div>}
            </div>
            <div style={{ fontSize:'12px', fontWeight:600, color:payColor, fontFamily:'var(--font-body)' }}>{payShort}</div>
          </div>

          {/* Row 5: Action pills */}
          {isPreorderTab ? (
            <div style={{ display:'flex', gap:'8px' }} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>handleFulfilPreorder(order)} disabled={isFulfilling}
                style={{ flex:1, padding:'9px', borderRadius:'var(--radius-md)', background:isFulfilling?'var(--border)':'#0D9488', border:'none', fontSize:'12.5px', fontWeight:700, color:'white', cursor:isFulfilling?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px', fontFamily:'var(--font-ui)' }}>
                <Icon path={IC.check2} color='white' size={12} sw={2.5}/>
                {isFulfilling?'Fulfilling...':'Fulfil'}
              </button>
              <button onClick={()=>navigate('/orders/'+order.id)}
                style={{ flex:1, padding:'9px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                View
              </button>
            </div>
          ) : (
            <div style={{ display:'flex', gap:'6px', paddingTop:'10px', borderTop:'1px solid var(--border)' }} onClick={e=>e.stopPropagation()}>
              <button onClick={()=>navigate('/orders/'+order.id)}
                style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px' }}>
                <Icon path={IC.eye} size={13} color='var(--text-primary)' sw={2}/> {t('orders.col_actions')}
              </button>
              <button onClick={()=>navigate('/orders/'+order.id+'?edit=1')}
                style={{ flex:1, padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)', display:'flex', alignItems:'center', justifyContent:'center', gap:'5px' }}>
                <Icon path={IC.edit} size={13} color='var(--text-primary)' sw={2}/> {t('common.edit')}
              </button>
              <button onClick={()=>navigate('/orders/'+order.id+'?receipt=1')}
                style={{ width:'40px', padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon path={IC.download} size={13} color='var(--success)' sw={2}/>
              </button>
              <button onClick={()=>window.print()}
                style={{ width:'40px', padding:'8px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <Icon path={IC.print} size={13} color='#6D28D9' sw={2}/>
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ── Status pills with per-status colours ────────────────────────────────────
  const STATUS_PILLS = [
    { key:'all',          label:'All',        count:mainOrders.length,                                    color:'var(--primary)',  bg:'var(--primary-light)' },
    { key:'received',     label:'Received',   count:mainOrders.filter(o=>o.status==='received').length,   color:'var(--primary)',  bg:'var(--primary-light)' },
    { key:'pending',      label:'Pending',    count:mainOrders.filter(o=>o.status==='pending').length,    color:'var(--warning)',  bg:'var(--warning-light)' },
    { key:'confirmed',    label:'Confirmed',  count:mainOrders.filter(o=>o.status==='confirmed').length,  color:'#92400E',        bg:'#FEF3C7' },
    { key:'processing',   label:'Processing', count:mainOrders.filter(o=>o.status==='processing').length, color:'var(--success)',  bg:'var(--success-light)' },
    { key:'with_courier', label:'Courier',    count:mainOrders.filter(o=>o.status==='with_courier').length,'color':'#6D28D9',     bg:'#F5F3FF' },
    { key:'shipped',      label:'Shipped',    count:mainOrders.filter(o=>o.status==='shipped').length,    color:'var(--success)',  bg:'var(--success-light)' },
    { key:'return',       label:'Return',     count:mainOrders.filter(o=>o.status==='return').length,     color:'var(--error)',    bg:'var(--error-light)' },
    { key:'closed',       label:'Closed',     count:mainOrders.filter(o=>o.status==='closed').length,     color:'var(--text-muted)', bg:'var(--bg-muted)' },
  ]

  return (
    <div className="flex-1 overflow-x-hidden pc-page" style={{ background:'var(--bg-page)', fontFamily:'var(--font-body)' }}>
      <Topbar title={t('orders.title')}/>

      <div style={{ padding:'20px 24px' }}>

        {/* ── Page header ── */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:'20px', flexWrap:'wrap', gap:'12px' }}>
          <div>
            <h1 style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', fontFamily:'var(--font-ui)', letterSpacing:'-0.3px', margin:0 }}>{t('orders.title')}</h1>
            <p style={{ fontSize:'13px', color:'var(--text-muted)', margin:'3px 0 0', fontFamily:'var(--font-body)' }}>
              {mainOrders.length} orders total{preOrders.length>0?` · ${preOrders.length} pre-orders`:''}
            </p>
          </div>
          <div style={{ display:'flex', gap:'8px', alignItems:'center', flexWrap:'wrap' }}>
            <button onClick={()=>setShowImport(true)}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)', transition:'var(--transition-fast)' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)';e.currentTarget.style.background='var(--bg-muted)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-card)'}}>
              <Icon path={IC.upload} size={13} color='var(--text-primary)' sw={2}/>
              {t('orders.import_csv')}
            </button>
            <button onClick={()=>setShowCourierExport(true)}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'8px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:'var(--text-primary)', cursor:'pointer', fontFamily:'var(--font-ui)', transition:'var(--transition-fast)' }}
              onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)';e.currentTarget.style.background='var(--bg-muted)'}}
              onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.background='var(--bg-card)'}}>
              <Icon path={IC.download} size={13} color='var(--text-primary)' sw={2}/>
              Courier Export
            </button>
            <button onClick={()=>navigate('/orders/add')}
              style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 18px', borderRadius:'var(--radius-md)', background:'var(--primary)', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', fontFamily:'var(--font-ui)', boxShadow:'0 2px 8px rgba(14,165,233,0.3)', transition:'var(--transition-base)' }}
              onMouseEnter={e=>{e.currentTarget.style.background='var(--primary-hover)';e.currentTarget.style.boxShadow='0 4px 12px rgba(14,165,233,0.4)'}}
              onMouseLeave={e=>{e.currentTarget.style.background='var(--primary)';e.currentTarget.style.boxShadow='0 2px 8px rgba(14,165,233,0.3)'}}>
              <Icon path={IC.plus} color='white' size={14} sw={2.5}/>
              {t('orders.new_order')}
            </button>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div style={{ display:'flex', gap:'3px', marginBottom:'20px', padding:'4px', borderRadius:'var(--radius-lg)', border:'1px solid var(--border)', background:'var(--bg-card)', width:'fit-content', boxShadow:'var(--shadow-sm)', flexWrap:'wrap' }}>
          {[
            { key:'orders',    label:t('orders.tab_orders'),   count:mainOrders.length,       alert:false },
            { key:'preorders', label:'Pre-orders',             count:preOrders.length,         alert:false, teal:true },
            { key:'tara',      label:'Tara Orders',            count:taraGroups.length,        alert:true,  tara:true },
            { key:'requests',  label:t('orders.tab_requests'), count:pendingRequests.length,   alert:true },
          ].map(tab=>(
            <button key={tab.key} onClick={()=>setActiveTab(tab.key)}
              style={{ display:'flex', alignItems:'center', gap:'7px', padding:'7px 16px', borderRadius:'var(--radius-md)', background:activeTab===tab.key?(tab.tara?'#0B1F3A':tab.teal?'#0D9488':'var(--primary)'):'transparent', color:activeTab===tab.key?'white':'var(--text-muted)', border:'none', cursor:'pointer', fontSize:'12.5px', fontWeight:600, transition:'var(--transition-base)', fontFamily:'var(--font-ui)' }}>
              {tab.key==='preorders'&&<Icon path={IC.clock} color={activeTab===tab.key?'white':'#0D9488'} size={13} sw={2}/>}
              {tab.key==='tara'&&<TaraStarIcon size={13} color={activeTab===tab.key?'#2DD4B0':'#0D9488'} />}
              {tab.label}
              {tab.count>0&&(
                <span style={{ padding:'1px 7px', borderRadius:'var(--radius-full)', fontSize:'10.5px', fontWeight:700, background:activeTab===tab.key?'rgba(255,255,255,0.25)':tab.alert?'var(--error)':tab.teal?'rgba(13,148,136,0.12)':'var(--bg-muted)', color:activeTab===tab.key?'white':tab.alert?'white':tab.teal?'#0D9488':'var(--text-muted)' }}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── ORDERS TAB ── */}
        {activeTab==='orders'&&(
          <>
            {/* Stat cards */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'12px', marginBottom:'16px' }} className="orders-stat-grid">
              <StatCard label={t('orders.total_orders')} value={mainOrders.length}  iconPath={FLAT.orders}  accentColor='var(--primary)'  loading={loading}/>
              <StatCard label={t('orders.paid')}         value={totalPaid}           iconPath={FLAT.paid}    accentColor='var(--success)'  loading={loading}/>
              <StatCard label={t('orders.pending')}      value={totalPending}        iconPath={FLAT.pending} accentColor='var(--warning)'  loading={loading}/>
              <StatCard label={t('orders.overdue')}      value={totalOverdue}        iconPath={FLAT.warning} accentColor='var(--error)'    loading={loading}/>
            </div>

            {/* Charts */}
            {orders.length>0&&(
              <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr', gap:'12px', marginBottom:'20px' }} className="orders-chart-grid">
                <div className="pc-card" style={{ padding:'16px 20px' }}>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-ui)' }}>{t('orders.orders_7days')}</div>
                  <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'12px', fontFamily:'var(--font-body)' }}>{t('orders.daily_volume')}</div>
                  <ResponsiveContainer width="100%" height={110}>
                    <LineChart data={dailyData} margin={{top:4,right:4,left:-20,bottom:0}}>
                      <XAxis dataKey="day" tick={{fontSize:10,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} axisLine={false} tickLine={false}/>
                      <YAxis tick={{fontSize:10,fill:'var(--text-muted)',fontFamily:'var(--font-body)'}} axisLine={false} tickLine={false} allowDecimals={false}/>
                      <Tooltip contentStyle={{ border:'1px solid var(--border)', borderRadius:'8px', fontFamily:'var(--font-body)', fontSize:'11px', background:'var(--bg-card)', color:'var(--text-primary)' }} formatter={v=>[v,t('orders.tab_orders')]}/>
                      <Line type="monotone" dataKey="count" stroke='var(--primary)' strokeWidth={2.5} dot={{fill:'var(--primary)',r:3,strokeWidth:0}} activeDot={{r:5,strokeWidth:0}}/>
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <div className="pc-card" style={{ padding:'16px 20px' }}>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)', marginBottom:'2px', fontFamily:'var(--font-ui)' }}>{t('orders.order_status')}</div>
                  <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'8px', fontFamily:'var(--font-body)' }}>{t('orders.status_breakdown')}</div>
                  <div style={{ display:'flex', alignItems:'center', gap:'12px' }}>
                    <ResponsiveContainer width={90} height={90}>
                      <PieChart>
                        <Pie data={statusData} cx="50%" cy="50%" innerRadius={26} outerRadius={42} paddingAngle={2} dataKey="value" strokeWidth={0}>
                          {statusData.map((entry,i)=><Cell key={entry.status} fill={PIE_COLORS[i%PIE_COLORS.length]}/>)}
                        </Pie>
                        <Tooltip contentStyle={{ border:'1px solid var(--border)', borderRadius:'8px', fontFamily:'var(--font-body)', fontSize:'11px', background:'var(--bg-card)', color:'var(--text-primary)' }} formatter={(v,n,p)=>[v,p.payload.label]}/>
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ flex:1, display:'flex', flexDirection:'column', gap:'5px' }}>
                      {statusData.slice(0,5).map((s,i)=>(
                        <div key={s.status} style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                          <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
                            <div style={{ width:'7px', height:'7px', borderRadius:'50%', background:PIE_COLORS[i%PIE_COLORS.length], flexShrink:0 }}/>
                            <span style={{ fontSize:'11px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{s.label}</span>
                          </div>
                          <span style={{ fontSize:'11px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{s.value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Status filter pills — coloured per status */}
            <div style={{ display:'flex', gap:'6px', marginBottom:'12px', overflowX:'auto', paddingBottom:'4px' }}>
              {STATUS_PILLS.map(s=>{
                const isActive = filterStatus===s.key
                return (
                  <button key={s.key} onClick={()=>setFilterStatus(s.key)}
                    style={{ display:'flex', alignItems:'center', gap:'5px', padding:'6px 14px', borderRadius:'var(--radius-full)', border:'1px solid', fontSize:'12px', fontWeight:600, whiteSpace:'nowrap', flexShrink:0, cursor:'pointer', transition:'var(--transition-fast)', fontFamily:'var(--font-ui)', background:isActive?s.bg:'var(--bg-card)', color:isActive?s.color:'var(--text-muted)', borderColor:isActive?s.color:'var(--border)' }}>
                    {s.label}
                    <span style={{ fontSize:'10.5px', fontWeight:700, padding:'1px 5px', borderRadius:'var(--radius-full)', background:isActive?s.color:'var(--bg-muted)', color:isActive?'white':'var(--text-muted)' }}>
                      {s.count}
                    </span>
                  </button>
                )
              })}
            </div>

            {/* Search + date + new order */}
            <div style={{ display:'flex', gap:'8px', marginBottom:'10px', flexWrap:'wrap' }}>
              <div style={{ flex:1, minWidth:'240px', position:'relative' }}>
                <input type="text" placeholder={t('orders.search_placeholder')} value={search} onChange={e=>setSearch(e.target.value)}
                  className="pc-input"
                  style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 36px 9px 36px', fontSize:'12.5px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
                  onFocus={e=>{e.target.style.borderColor='var(--border-focus)';e.target.style.background='var(--bg-card)';e.target.style.boxShadow='0 0 0 3px var(--primary-light)'}}
                  onBlur={e=>{e.target.style.borderColor='var(--border)';e.target.style.background='var(--bg-muted)';e.target.style.boxShadow='none'}}/>
                <div style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)' }}>
                  <Icon path={IC.search} color='var(--text-muted)' size={14}/>
                </div>
                {search&&(
                  <button onClick={()=>setSearch('')} style={{ position:'absolute', right:'10px', top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'14px' }}>×</button>
                )}
              </div>
              <button onClick={()=>setShowDateFilter(!showDateFilter)}
                style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 14px', borderRadius:'var(--radius-md)', border:'1px solid '+(hasDateFilter?'var(--border-focus)':'var(--border)'), background:hasDateFilter?'var(--primary-light)':'var(--bg-card)', fontSize:'12.5px', fontWeight:600, color:hasDateFilter?'var(--primary)':'var(--text-primary)', cursor:'pointer', flexShrink:0, fontFamily:'var(--font-ui)' }}>
                <Icon path={IC.calendar} size={13} color={hasDateFilter?'var(--primary)':'var(--text-primary)'} sw={2}/>
                {hasDateFilter?`${dateFrom||'...'} → ${dateTo||'...'}`:t('orders.date_filter')||'Date filter'}
                {hasDateFilter&&<span onClick={e=>{e.stopPropagation();resetDateFilter()}} style={{ marginLeft:'2px', fontSize:'14px', color:'var(--primary)' }}>×</span>}
              </button>
              <ColumnsMenu visible={visibleCols} setVisible={setVisibleCols} open={columnsOpen} setOpen={setColumnsOpen}/>
            </div>

            {/* Date range picker */}
            {showDateFilter&&(
              <div className="pc-card" style={{ padding:'14px 16px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'12px', flexWrap:'wrap' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', whiteSpace:'nowrap', fontFamily:'var(--font-ui)' }}>From</label>
                  <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
                    style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}
                    onFocus={e=>e.target.style.borderColor='var(--border-focus)'}
                    onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                  <label style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', whiteSpace:'nowrap', fontFamily:'var(--font-ui)' }}>To</label>
                  <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
                    style={{ border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'7px 10px', fontSize:'12.5px', outline:'none', color:'var(--text-primary)', fontFamily:'var(--font-body)', background:'var(--bg-card)', cursor:'pointer' }}
                    onFocus={e=>e.target.style.borderColor='var(--border-focus)'}
                    onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                </div>
                <button onClick={resetDateFilter}
                  style={{ padding:'7px 14px', borderRadius:'var(--radius-md)', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'12px', fontWeight:600, color:'var(--text-muted)', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                  Reset
                </button>
                {hasDateFilter&&<div style={{ fontSize:'12px', color:'var(--primary)', fontWeight:600, fontFamily:'var(--font-body)' }}>{filtered.length} order{filtered.length!==1?'s':''} in range</div>}
              </div>
            )}

            {search&&<div style={{ fontSize:'12px', color:'var(--text-muted)', marginBottom:'10px', fontFamily:'var(--font-body)' }}>{filtered.length} {t('orders.results_for')} "{search}"</div>}

            {/* Table */}
            {loading ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'spin 0.7s linear infinite', margin:'0 auto 12px' }}/>
                <div style={{ fontSize:'13px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{t('orders.loading')}</div>
              </div>
            ) : filtered.length===0 ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'var(--bg-muted)', border:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <Icon path={IC.orders} color='var(--text-muted)' size={24} sw={1.5}/>
                </div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>
                  {mainOrders.length===0?t('orders.no_orders_yet'):t('orders.no_orders_match')}
                </div>
                <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'16px', fontFamily:'var(--font-body)' }}>
                  {mainOrders.length===0?'Create your first order to get started':'Try adjusting your search or filters'}
                </div>
                {mainOrders.length===0&&(
                  <button onClick={()=>navigate('/orders/add')}
                    style={{ fontSize:'12.5px', fontWeight:600, padding:'9px 20px', borderRadius:'var(--radius-md)', background:'var(--primary)', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                    {t('orders.add_first')}
                  </button>
                )}
              </div>
            ) : (
              <div className="pc-card" style={{ overflow:'hidden' }}>
                {/* Scrollable table with sticky header */}
                <div className="orders-table-scroll" style={{ overflowX:'auto', overflowY:'auto', maxHeight:'640px' }}>
                  <div className="orders-table-wrap" style={{ minWidth:TABLE_MIN_WIDTH+'px' }}>
                    <div className="orders-row"
                      style={{ display:'grid', gridTemplateColumns:TABLE_COLS, gap:TABLE_GAP+'px', padding:'10px 18px', background:'var(--bg-muted)', borderBottom:'1px solid var(--border)', position:'sticky', top:0, zIndex:10 }}>
                      {visibleCols.sl&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('orders.col_sl')}</div>}
                      {visibleCols.orderId&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('orders.col_order_id')}</div>}
                      {visibleCols.customer&&<FilterableHeader label={t('orders.col_customer')} filterKey="customer" openFilter={openFilter} setOpenFilter={setOpenFilter} value={colFilters.customer} onChange={v=>setColFilters(f=>({...f,customer:v}))} active={!!colFilters.customer}/>}
                      {visibleCols.product&&<FilterableHeader label={t('orders.col_product')} filterKey="product" openFilter={openFilter} setOpenFilter={setOpenFilter} value={colFilters.product} onChange={v=>setColFilters(f=>({...f,product:v}))} active={!!colFilters.product}/>}
                      {visibleCols.location&&<FilterableHeader label={t('orders.col_location')} filterKey="location" openFilter={openFilter} setOpenFilter={setOpenFilter} value={colFilters.location} onChange={v=>setColFilters(f=>({...f,location:v}))} active={!!colFilters.location}/>}
                      {visibleCols.date&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('orders.col_date')}</div>}
                      {visibleCols.amount&&<FilterableHeader label={t('orders.col_amount')} filterKey="amount" openFilter={openFilter} setOpenFilter={setOpenFilter} type="range" value={{min:colFilters.amountMin,max:colFilters.amountMax}} onChange={v=>setColFilters(f=>({...f,amountMin:v.min,amountMax:v.max}))} active={!!(colFilters.amountMin||colFilters.amountMax)}/>}
                      {visibleCols.status&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('orders.col_status')}</div>}
                      {visibleCols.actions&&<div style={{ fontSize:'10.5px', fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{t('orders.col_actions')}</div>}
                    </div>
                    {pageOrders.map((order,idx)=>renderOrderRow(order, pageStart+idx+1, false))}
                  </div>
                </div>

                {/* Pagination */}
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 18px', borderTop:'1px solid var(--border)', background:'var(--bg-muted)', flexWrap:'wrap', gap:'8px' }}>
                  <div style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>
                    Showing <strong style={{ color:'var(--text-primary)' }}>{pageStart+1}–{pageEnd}</strong> of <strong style={{ color:'var(--text-primary)' }}>{filtered.length}</strong>
                  </div>
                  {totalPages>1&&(
                    <div style={{ display:'flex', alignItems:'center', gap:'4px' }}>
                      <button onClick={()=>setCurrentPage(p=>Math.max(1,p-1))} disabled={currentPage===1}
                        style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===1?'not-allowed':'pointer', opacity:currentPage===1?0.4:1 }}>
                        <Icon path={IC.chevL} color='var(--text-primary)' size={13} sw={2}/>
                      </button>
                      {Array.from({length:Math.min(totalPages,7)},(_,i)=>{
                        let page
                        if(totalPages<=7) page=i+1
                        else if(currentPage<=4) page=i+1
                        else if(currentPage>=totalPages-3) page=totalPages-6+i
                        else page=currentPage-3+i
                        if(page<1||page>totalPages) return null
                        return (
                          <button key={page} onClick={()=>setCurrentPage(page)}
                            style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid '+(currentPage===page?'var(--primary)':'var(--border)'), background:currentPage===page?'var(--primary)':'var(--bg-card)', color:currentPage===page?'white':'var(--text-primary)', fontSize:'12px', fontWeight:currentPage===page?700:400, cursor:'pointer', fontFamily:'var(--font-body)' }}>
                            {page}
                          </button>
                        )
                      })}
                      <button onClick={()=>setCurrentPage(p=>Math.min(totalPages,p+1))} disabled={currentPage===totalPages}
                        style={{ width:'30px', height:'30px', borderRadius:'var(--radius-sm)', border:'1px solid var(--border)', background:'var(--bg-card)', display:'flex', alignItems:'center', justifyContent:'center', cursor:currentPage===totalPages?'not-allowed':'pointer', opacity:currentPage===totalPages?0.4:1 }}>
                        <Icon path={IC.chevR} color='var(--text-primary)' size={13} sw={2}/>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── PRE-ORDERS TAB ── */}
        {activeTab==='preorders'&&(
          <>
            <div style={{ background:'rgba(13,148,136,0.08)', border:'1px solid rgba(13,148,136,0.2)', borderRadius:'var(--radius-lg)', padding:'14px 18px', marginBottom:'16px', display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:'12px' }}>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'#0D9488', marginBottom:'3px', fontFamily:'var(--font-ui)' }}>Pre-order Pipeline</div>
                <div style={{ fontSize:'12.5px', color:'#0F766E', fontFamily:'var(--font-body)' }}>
                  {preOrders.length} pre-order{preOrders.length!==1?'s':''} · Pipeline value: <strong>{fmt(preOrderPipelineValue)}</strong>
                </div>
                <div style={{ fontSize:'11.5px', color:'#0F766E', marginTop:'3px', fontFamily:'var(--font-body)' }}>Stock deducts only when you click Fulfil.</div>
              </div>
              <button onClick={()=>navigate('/orders/add?preorder=true')}
                style={{ display:'flex', alignItems:'center', gap:'6px', padding:'9px 18px', borderRadius:'var(--radius-md)', background:'#0D9488', border:'none', fontSize:'13px', fontWeight:600, color:'white', cursor:'pointer', flexShrink:0, fontFamily:'var(--font-ui)' }}>
                <Icon path={IC.plus} color='white' size={14} sw={2.5}/>
                New Pre-order
              </button>
            </div>

            <div style={{ position:'relative', marginBottom:'14px' }}>
              <input type="text" placeholder="Search pre-orders..." value={search} onChange={e=>setSearch(e.target.value)}
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'var(--radius-md)', padding:'9px 12px 9px 36px', fontSize:'12.5px', outline:'none', background:'var(--bg-muted)', color:'var(--text-primary)', fontFamily:'var(--font-body)', boxSizing:'border-box' }}
                onFocus={e=>e.target.style.borderColor='var(--border-focus)'}
                onBlur={e=>e.target.style.borderColor='var(--border)'}/>
              <div style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)' }}>
                <Icon path={IC.search} color='var(--text-muted)' size={14}/>
              </div>
            </div>

            {loading ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid #0D9488', borderTopColor:'transparent', animation:'spin 0.7s linear infinite', margin:'0 auto' }}/>
              </div>
            ) : filteredPreorders.length===0 ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'rgba(13,148,136,0.08)', border:'1px solid rgba(13,148,136,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <Icon path={IC.clock} color='#0D9488' size={24} sw={1.5}/>
                </div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'6px', fontFamily:'var(--font-ui)' }}>No pre-orders yet</div>
                <div style={{ fontSize:'12.5px', color:'var(--text-muted)', marginBottom:'16px', fontFamily:'var(--font-body)' }}>Take payments before stock arrives. Stock only deducts when you fulfil.</div>
                <button onClick={()=>navigate('/orders/add?preorder=true')}
                  style={{ fontSize:'12.5px', fontWeight:600, padding:'9px 20px', borderRadius:'var(--radius-md)', background:'#0D9488', color:'white', border:'none', cursor:'pointer', fontFamily:'var(--font-ui)' }}>
                  Create First Pre-order
                </button>
              </div>
            ) : (
              <div className="pc-card" style={{ overflow:'hidden' }}>
                <div className="orders-row"
                  style={{ display:'grid', gridTemplateColumns:PREORDER_COLS, gap:TABLE_GAP+'px', padding:'10px 18px', background:'rgba(13,148,136,0.06)', borderBottom:'1px solid rgba(13,148,136,0.15)' }}>
                  {['#','Order ID','Customer','Product','Location','Ordered','Amount','','Action'].map(h=>(
                    <div key={h} style={{ fontSize:'10.5px', fontWeight:700, color:'#0D9488', textTransform:'uppercase', letterSpacing:'0.06em', fontFamily:'var(--font-ui)' }}>{h}</div>
                  ))}
                </div>
                {filteredPreorders.map((order,idx)=>renderOrderRow(order,idx+1,true))}
              </div>
            )}
          </>
        )}

        {/* ── TARA ORDERS TAB ── */}
        {activeTab==='tara'&&(
          <>
            <div style={{ background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', borderRadius:'var(--radius-lg)', padding:'14px 18px', marginBottom:'16px', display:'flex', alignItems:'center', gap:'12px' }}>
              <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'rgba(255,255,255,0.1)', border:'1.5px solid rgba(45,212,176,0.4)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                <TaraStarIcon size={18} />
              </div>
              <div>
                <div style={{ fontSize:'13px', fontWeight:700, color:'white', fontFamily:'var(--font-ui)' }}>Orders from Quick Order Form</div>
                <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.6)', marginTop:'2px', fontFamily:'var(--font-body)' }}>
                  Submitted by customers via your order form link — review and confirm to add to Orders
                </div>
              </div>
            </div>

            {loadingTara ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid #0D9488', borderTopColor:'transparent', animation:'spin 0.7s linear infinite', margin:'0 auto' }}/>
              </div>
            ) : taraGroups.length===0 ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'rgba(45,212,176,0.08)', border:'1px solid rgba(45,212,176,0.2)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <TaraStarIcon size={26} color="#0D9488" />
                </div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>No pending Tara orders</div>
                <div style={{ fontSize:'12.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>New submissions from your order form will appear here</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                {taraGroups.map(group => (
                  <TaraOrderCard
                    key={group.id}
                    group={group}
                    onConfirm={handleConfirmTaraOrder}
                    onReject={handleRejectTaraOrder}
                    processingId={processingId}
                    sym={sym}
                    expandedId={expandedTara}
                    setExpandedId={setExpandedTara}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {/* ── REQUESTS TAB ── */}
        {activeTab==='requests'&&(
          <>
            <div style={{ borderRadius:'var(--radius-md)', padding:'12px 16px', marginBottom:'16px', background:'var(--primary-light)', border:'1px solid var(--primary-border)', fontSize:'12.5px', color:'var(--primary)', fontFamily:'var(--font-body)' }}>
              <div style={{ fontWeight:700, marginBottom:'3px', fontFamily:'var(--font-ui)' }}>{t('orders.customer_requests')}</div>
              {t('orders.request_info')}
            </div>

            {loadingRequests ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'24px', height:'24px', borderRadius:'50%', border:'2.5px solid var(--primary)', borderTopColor:'transparent', animation:'spin 0.7s linear infinite', margin:'0 auto' }}/>
              </div>
            ) : pendingRequests.length===0 ? (
              <div className="pc-card" style={{ padding:'48px', textAlign:'center' }}>
                <div style={{ width:'56px', height:'56px', borderRadius:'var(--radius-lg)', background:'var(--success-light)', border:'1px solid var(--success-border)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 14px' }}>
                  <svg width="24" height="24" viewBox="0 0 20 20" fill="var(--success)"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/></svg>
                </div>
                <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)', marginBottom:'4px', fontFamily:'var(--font-ui)' }}>{t('orders.all_caught_up')}</div>
                <div style={{ fontSize:'12.5px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>{t('orders.no_pending_requests')}</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'12px' }}>
                {pendingRequests.map(req=>{
                  const isExpanded   = expandedRequest===req.id
                  const isProcessing = processingId===req.id
                  return (
                    <div key={req.id} className="pc-card" style={{ overflow:'hidden' }}>
                      <div style={{ padding:'14px 18px', background:'linear-gradient(135deg,#0B1F3A,#1a3a6e)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div>
                          <div style={{ fontSize:'13px', fontWeight:700, color:'white', fontFamily:'var(--font-ui)' }}>{req.customer_name||'Unknown customer'}</div>
                          <div style={{ fontSize:'12px', color:'rgba(255,255,255,0.65)', marginTop:'2px', fontFamily:'var(--font-body)' }}>{req.customer_phone}</div>
                        </div>
                        <div style={{ fontSize:'11.5px', color:'rgba(255,255,255,0.5)', textAlign:'right', fontFamily:'var(--font-body)' }}>
                          {req.submitted_at?new Date(req.submitted_at).toLocaleDateString('en-BD',{day:'numeric',month:'short'})+' '+new Date(req.submitted_at).toLocaleTimeString('en-BD',{hour:'2-digit',minute:'2-digit'}):t('orders.just_now')}
                        </div>
                      </div>
                      <div style={{ padding:'16px 18px' }}>
                        {req.product_code ? (
                          <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'10px' }}>
                            <span style={{ fontSize:'12px', color:'var(--text-muted)', fontFamily:'var(--font-body)' }}>Product code:</span>
                            <span style={{ fontSize:'12.5px', fontWeight:700, color:'var(--text-primary)', fontFamily:'var(--font-mono)' }}>{req.product_code}</span>
                          </div>
                        ) : (
                          <div style={{ fontSize:'12px', color:'var(--warning)', marginBottom:'10px', fontWeight:500, fontFamily:'var(--font-body)' }}>{t('orders.no_product_code')}</div>
                        )}
                        <div style={{ marginBottom:'10px' }}>
                          <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'4px', fontFamily:'var(--font-body)' }}>{t('orders.delivery_address')}</div>
                          <div style={{ fontSize:'12.5px', fontWeight:500, color:'var(--text-primary)', lineHeight:1.6, fontFamily:'var(--font-body)' }}>{formatAddress(req)||t('orders.no_address')}</div>
                        </div>
                        {req.notes&&(
                          <div style={{ marginBottom:'10px' }}>
                            <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginBottom:'4px', fontFamily:'var(--font-body)' }}>{t('orders.notes')}</div>
                            <div style={{ fontSize:'12.5px', color:'var(--text-primary)', fontFamily:'var(--font-body)' }}>{req.notes}</div>
                          </div>
                        )}
                        <button onClick={()=>setExpandedRequest(isExpanded?null:req.id)}
                          style={{ fontSize:'12px', fontWeight:600, color:'var(--primary)', background:'none', border:'none', cursor:'pointer', padding:0, fontFamily:'var(--font-ui)' }}>
                          {isExpanded?t('orders.hide_details'):t('orders.view_details')}
                        </button>
                        {isExpanded&&(
                          <div style={{ marginTop:'10px', borderRadius:'var(--radius-md)', padding:'12px', background:'var(--bg-muted)', border:'1px solid var(--border)' }}>
                            {[
                              {label:'Country',value:req.country},{label:'Division',value:req.division},{label:'District',value:req.district},
                              {label:'Upazilla',value:req.thana},{label:'Union',value:req.union_name},{label:'Neighbourhood',value:req.neighbourhood},
                              {label:'Moholla',value:req.moholla},{label:'Road',value:req.road},{label:'House',value:req.house},{label:'ZIP',value:req.zip},
                            ].filter(f=>f.value&&f.value!=='N/A').map((f,i)=>(
                              <div key={i} style={{ display:'flex', gap:'8px', marginBottom:'4px', fontSize:'12px', fontFamily:'var(--font-body)' }}>
                                <span style={{ color:'var(--text-muted)', flexShrink:0, width:'90px' }}>{f.label}:</span>
                                <span style={{ color:'var(--text-primary)' }}>{f.value}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ padding:'0 18px 18px', display:'grid', gridTemplateColumns:'1fr 1fr', gap:'10px' }}>
                        <button onClick={()=>handleDecline(req)} disabled={isProcessing}
                          style={{ padding:'10px', borderRadius:'var(--radius-md)', fontSize:'12.5px', fontWeight:600, border:'1px solid var(--error-border)', background:'var(--error-light)', color:'var(--error)', cursor:'pointer', opacity:isProcessing?0.5:1, fontFamily:'var(--font-ui)' }}>
                          {isProcessing?'...':t('orders.decline')}
                        </button>
                        <button onClick={()=>handleApprove(req)} disabled={isProcessing}
                          style={{ padding:'10px', borderRadius:'var(--radius-md)', fontSize:'12.5px', fontWeight:600, border:'none', background:isProcessing?'var(--border)':'var(--primary)', color:'white', cursor:'pointer', opacity:isProcessing?0.5:1, fontFamily:'var(--font-ui)' }}>
                          {isProcessing?t('orders.processing'):t('orders.approve')}
                        </button>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </>
        )}
      </div>

      {showImport&&<ImportModal onClose={()=>setShowImport(false)} onImport={handleImport} t={t}/>}
      {showCourierExport&&<CourierExportModal orders={orders} onClose={()=>setShowCourierExport(false)} sym={sym}/>}

      <style>{`
        @keyframes spin { to { transform:rotate(360deg) } }
        @media(max-width:900px){
          .orders-stat-grid { grid-template-columns:1fr 1fr!important }
          .orders-chart-grid { grid-template-columns:1fr!important }
        }
        @media(max-width:768px){
          .orders-row { display:none!important }
          .orders-mobile-card { display:block!important }
          .orders-table-wrap { min-width:0!important }
          .orders-table-scroll { overflow-x:visible!important }
        }
        @media(max-width:540px){
          .orders-stat-grid { grid-template-columns:1fr 1fr!important }
        }
      `}</style>
    </div>
  )
}

export default Orders